import { format } from 'date-fns';
import { fg } from '@atlassian/jira-feature-gating';
import type {
	IssueCreateIssueType,
	IssueCreateProject,
	IssueToCreate,
	IssueCreateAssignee,
} from '@atlassian/jira-issue-table-inline-issue-create/src/common/types.tsx';
import { toOptimisticField } from '@atlassian/jira-issue-table-grouping-issue-create-utils/src/index.tsx';
import type { GroupContextField } from '@atlassian/jira-issue-table-grouping-issue-create-utils/src/types.tsx';
import type { CreateIssueControllerMutation$rawResponse as OptimisticResponse } from '../__generated__/CreateIssueControllerMutation.graphql';
import type { OptimisticFieldSet } from './types.tsx';

export const toOptimisticResponse = (
	{ project, issueType, summary, assignee, dueDate }: IssueToCreate,
	optimisticId: string,
	isIssueHierarchyEnabled: boolean,
	groupContextFieldInput?: GroupContextField,
): OptimisticResponse => {
	const issueId = optimisticId;
	const summaryEdge = summary ? toOptimisticSummary(issueId, summary) : null;
	const projectEdge = project ? toOptimisticProject(issueId, project) : null;
	const issueTypeEdge = issueType ? toOptimisticIssueType(issueId, issueType) : null;
	const assigneeEdge =
		assignee && fg('jira_render_pic_in_nin') ? toOptimisticAssignee(issueId, assignee) : null;
	const dueDateEdge =
		dueDate && fg('jira_render_pic_in_nin') ? toOptimisticDueDate(issueId, dueDate) : null;

	const edges = [summaryEdge, projectEdge, issueTypeEdge, assigneeEdge, dueDateEdge].filter(
		(edge) => edge !== null,
	);

	if (groupContextFieldInput) {
		if (fg('jira_render_pic_in_nin')) {
			if (groupContextFieldInput.fieldId !== 'assignee' || assigneeEdge === null) {
				const groupContextFieldEdge = toOptimisticField(issueId, groupContextFieldInput);
				// @ts-expect-error - TS2345 Typescript 5.9.2 upgrade
				edges.push(groupContextFieldEdge);
			}
		} else {
			const groupContextFieldEdge = toOptimisticField(issueId, groupContextFieldInput);
			// @ts-expect-error - TS2345 Typescript 5.9.2 upgrade
			edges.push(groupContextFieldEdge);
		}
	}

	return {
		jira: {
			createIssue: {
				errors: null,
				issue: {
					__typename: 'JiraIssue',
					id: issueId,
					issueId,
					key: '',
					fieldsByIdOrAlias: [],
					fieldSetsById: {
						edges,
					},
					matchesIssueSearch: undefined,
					...(isIssueHierarchyEnabled
						? {
								issueTypeField: {
									issueType: {
										hierarchy: {
											level: issueType?.hierarchy?.level,
										},
										id: issueType?.id,
									},
									id: '',
								},
								canHaveChildIssues: false,
							}
						: {
								issueTypeField: undefined,
								canHaveChildIssues: undefined,
							}),
				},
			},
		},
	};
};

const toOptimisticSummary = (issueId: string, summary: string): OptimisticFieldSet => ({
	node: {
		fieldSetId: 'summary',
		type: 'summary',
		fields: {
			totalCount: 1,
			edges: [
				{
					node: {
						__typename: 'JiraSingleLineTextField',
						__isJiraIssueField: 'JiraSingleLineTextField',
						fieldId: 'summary',
						id: `${issueId}/summary`,
						name: 'Summary', // TODO Do we need to translate?
						text: summary,
						type: 'summary',
						lazyIsEditableInIssueView: undefined,
					},
				},
			],
		},
	},
});

const toOptimisticIssueType = (
	issueId: string,
	issueType: IssueCreateIssueType,
): OptimisticFieldSet => ({
	node: {
		fieldSetId: 'issuetype',
		type: 'issuetype',
		fields: {
			totalCount: 1,
			edges: [
				{
					node: {
						__typename: 'JiraIssueTypeField',
						__isJiraIssueField: 'JiraIssueTypeField',
						fieldId: 'issuetype',
						id: `${issueId}/issuetype`,
						type: 'issuetype',
						issueType: {
							id: issueType.id,
							...(fg('jira_list_conditional_formatting') && { issueTypeId: issueType.issueTypeId }),
							name: issueType.name,
							avatar: issueType.avatar,
						},
					},
				},
			],
		},
	},
});

const toOptimisticProject = (issueId: string, project: IssueCreateProject): OptimisticFieldSet => ({
	node: {
		fieldSetId: 'project',
		type: 'project',
		fields: {
			totalCount: 1,
			edges: [
				{
					node: {
						__typename: 'JiraProjectField',
						__isJiraIssueField: 'JiraProjectField',
						fieldId: 'project',
						id: `${issueId}/project`,
						type: 'project',
						project: {
							id: project.id,
							...(fg('jira_list_conditional_formatting') && { projectId: project.projectId }),
							key: project.key,
							name: project.name,
							avatar: project.avatar,
						},
					},
				},
			],
		},
	},
});

const toOptimisticAssignee = (
	issueId: string,
	{ accountId, name, picture }: IssueCreateAssignee,
): OptimisticFieldSet => ({
	node: {
		fieldSetId: 'assignee',
		type: 'assignee',
		fields: {
			totalCount: 1,
			edges: [
				{
					node: {
						__typename: 'JiraSingleSelectUserPickerField',
						__isJiraIssueField: 'JiraSingleSelectUserPickerField',
						fieldId: 'assignee',
						id: `${issueId}/assignee`,
						type: 'assignee',
						name: 'Assignee',
						user: {
							id: accountId,
							accountId,
							name,
							picture,
							accountStatus: 'active',
							__typename: 'AtlassianAccountUser',
							__isUser: 'AtlassianAccountUser',
						},
					},
				},
			],
		},
	},
});

const toOptimisticDueDate = (issueId: string, dueDate: Date): OptimisticFieldSet => ({
	node: {
		fieldSetId: 'duedate',
		type: 'duedate',
		fields: {
			totalCount: 1,
			edges: [
				{
					node: {
						__typename: 'JiraDatePickerField',
						__isJiraIssueField: 'JiraDatePickerField',
						fieldId: 'duedate',
						id: `${issueId}/duedate`,
						type: 'duedate',
						date: format(dueDate, 'yyyy-MM-dd'),
						name: 'Due Date',
						fieldConfig: {
							isRequired: false,
						},
					},
				},
			],
		},
	},
});
